/**
 * AetherAuthContext
 * Provides authentication state + helpers for the Aether Dashboard.
 * Token is stored in localStorage (key: "aether_token") and sent as Bearer.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const TOKEN_KEY = 'aether_token';
const API = process.env.REACT_APP_BACKEND_URL;

const AetherAuthContext = createContext(null);

export const useAetherAuth = () => {
  const ctx = useContext(AetherAuthContext);
  if (!ctx) throw new Error('useAetherAuth must be used inside <AetherAuthProvider>');
  return ctx;
};

export function AetherAuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown / not logged in; object = authed
  const [loading, setLoading] = useState(true);

  const getToken = useCallback(() => localStorage.getItem(TOKEN_KEY), []);

  const setToken = useCallback((token) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  }, []);

  /** Tear down EVERY piece of session state at once. Used by all the logout
   *  paths (bad token at load, 401 mid-session, focus re-check, manual logout)
   *  so none can leave a stray flag that makes the UI look half-logged-in. */
  const clearSession = useCallback(() => {
    setToken(null);
    setUser(null);
    try {
      localStorage.removeItem('aether_admin_authenticated');
      localStorage.removeItem('aether_license_token');
    } catch { /* ignore */ }
  }, [setToken]);

  const fetchMe = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const res = await fetch(`${API}/api/aether/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        clearSession();
        return null;
      }
      const data = await res.json();
      setUser(data);
      return data;
    } catch {
      clearSession();
      return null;
    } finally {
      setLoading(false);
    }
  }, [API, getToken, clearSession]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  /** Re-check the session when the tab regains focus. A token can expire while
   *  the tab sits in the background — without this the user comes back to a
   *  dashboard that looks logged in but 401s on every call (the zombie state).
   *  Only a REAL 401 clears the session; a transient network error keeps it, so
   *  a brief offline moment doesn't kick the user out. */
  const revalidateSession = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const res = await fetch(`${API}/api/aether/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        clearSession();
      } else if (res.ok) {
        setUser(await res.json());
      }
      // 5xx / other: leave the session untouched
    } catch {
      /* network blip — keep the session, don't punish the user */
    }
  }, [API, getToken, clearSession]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'hidden') revalidateSession();
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [revalidateSession]);

  /** Step 1: email + password → triggers OTP email. Returns { ok, email_delivered } */
  const requestOtp = useCallback(async (email, password) => {
    const res = await fetch(`${API}/api/aether/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Login fehlgeschlagen');
    return data;
  }, [API]);

  /** Step 2: verify OTP → stores token and sets user */
  const verifyOtp = useCallback(async (email, code, remember) => {
    const res = await fetch(`${API}/api/aether/auth/verify-2fa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code, remember }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Code falsch');
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, [API, setToken]);

  /** Sign in via Google Identity Services — bypasses OTP (Google enforces 2FA itself). */
  const loginWithGoogle = useCallback(async (idToken, remember = true) => {
    const res = await fetch(`${API}/api/aether/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: idToken, remember }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Google-Login fehlgeschlagen');
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, [API, setToken]);

  const resendOtp = useCallback(async (email, password) => {
    const res = await fetch(`${API}/api/aether/auth/resend-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Resend fehlgeschlagen');
    return data;
  }, [API]);

  /** Public forgot-password flow */
  /** Offene Registrierung — legt ein Kundenkonto an. Gibt KEINE Sitzung zurueck:
   *  danach laeuft die normale Anmeldung mit Einmalcode, damit die Adresse belegt
   *  ist, bevor jemand hineinkommt. */
  const register = useCallback(async (email, password, name) => {
    const res = await fetch(`${API}/api/aether/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || 'Registrierung fehlgeschlagen');
    return data;
  }, [API]);

  /** Konto löschen. Bestellungen bleiben (Aufbewahrungspflicht), der Benutzer geht. */
  const deleteAccount = useCallback(async (confirm) => {
    const res = await authFetch('/api/aether/auth/me/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || 'Konto konnte nicht gelöscht werden');
    clearSession();
    return data;
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    const res = await fetch(`${API}/api/aether/auth/password-reset/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Anfrage fehlgeschlagen');
    return data;
  }, [API]);

  const confirmPasswordReset = useCallback(async (token, newPassword) => {
    const res = await fetch(`${API}/api/aether/auth/password-reset/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Reset fehlgeschlagen');
    return data;
  }, [API]);

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  /** Authenticated fetch helper. Auto-logs out on 401 so an expired token
   *  doesn't leave the dashboard in a zombie state where everything fails
   *  but the UI still pretends the user is signed in. */
  const authFetch = useCallback(async (path, options = {}) => {
    const token = getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    const res = await fetch(`${API}${path}`, { ...options, headers });
    // Auto-logout on ANY 401. authFetch always carries the aether SESSION
    // token (the license-admin section uses its own raw fetch, not this), so a
    // 401 here always means the session token is expired/invalid — regardless
    // of endpoint (/api/aether, /api/me, /api/dashboard, /api/admin). The old
    // check only fired for /api/aether, so an expired token during a
    // /api/me/library or /api/dashboard call left the dashboard "logged in"
    // with no data — the zombie state. (Wrong current password is 400, not
    // 401, so this can't log a user out mid password-change.)
    if (res.status === 401) {
      clearSession();
    }
    return res;
  }, [API, getToken, clearSession]);

  /** Self-service: update own display name. Refreshes the cached user. */
  /** Nimmt entweder einen Namen oder ein Objekt ({name} / {avatar}) — die
   *  Profilseite aendert mal das eine, mal das andere. */
  const updateProfile = useCallback(async (aenderung) => {
    const koerper = typeof aenderung === 'string' ? { name: aenderung } : aenderung;
    const res = await authFetch('/api/aether/auth/me', {
      method: 'PATCH',
      body: JSON.stringify(koerper),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Speichern fehlgeschlagen');
    setUser(data);
    return data;
  }, [authFetch]);

  /** Self-service: change own password. The backend re-issues a token (the
   *  session_version bump logs out every OTHER session) — store it so THIS
   *  session stays signed in. */
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const res = await authFetch('/api/aether/auth/me/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Passwortänderung fehlgeschlagen');
    if (data.token) setToken(data.token);
    return data;
  }, [authFetch, setToken]);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isPartner: user?.role === 'partner',
    isCustomer: user?.role === 'customer' || (user && !user.role),
    role: user?.role || 'customer',
    requestOtp,
    verifyOtp,
    resendOtp,
    loginWithGoogle,
    register,
    deleteAccount,
    requestPasswordReset,
    confirmPasswordReset,
    logout,
    authFetch,
    updateProfile,
    changePassword,
    refresh: fetchMe,
  };

  return (
    <AetherAuthContext.Provider value={value}>
      {children}
    </AetherAuthContext.Provider>
  );
}
